/**
 * Biggapon poster template themes.
 *
 * Each theme is a colour palette consumed by <PosterBase>. Designs live entirely
 * in frontend code; a job only stores the theme `id` (e.g. "green-gold"). Colours
 * are applied via inline styles because they are dynamic per-theme (they can't be
 * static Tailwind classes).
 */

// Build a full theme from a small set of base colours.
// `c.layout` picks the structural layout: 'classic' | 'featured' | 'banner'.
// `c.light: true` builds a light-background variant (white/cream card, dark text,
// accent-coloured headings) instead of the default dark poster.
function makeTheme(id, name, c) {
    if (c.light) return makeLightTheme(id, name, c);
    const accentText = c.accentText || '#1b1206';
    return {
        id,
        name,
        layout: c.layout || 'classic',
        orientation: c.orientation || 'portrait',
        light: false,
        posterBg: `linear-gradient(180deg, ${c.mid} 0%, ${c.deep} 62%, ${c.deep} 100%)`,
        frame: c.frame || c.accent,
        title: c.accent,
        subtitle: 'rgba(255,255,255,0.92)',
        logoBg: '#ffffff',
        logoBorder: c.accent,
        logoText: c.deep,
        badgeBg: c.badge || '#C0392B',
        badgeText: '#ffffff',
        badgeRing: c.accent,
        pillBg: 'rgba(0,0,0,0.42)',
        pillLabel: '#ffffff',
        pillValueBg: c.accent,
        pillValueText: accentText,
        boxBg: c.panel || '#F4F7EF',
        boxBorder: c.accent,
        boxLabel: c.boxLabel || c.mid,
        boxValue: c.boxValue || '#141a17',
        vacancyBg: c.vacancyPanel || '#FBF6E3',
        vacancyBorder: c.accent,
        vacancyNum: c.number,
        promo1: '#f4fbf7',
        promo2: c.accent,
        contactBg: `linear-gradient(180deg, ${c.contactMid || c.mid}, ${c.contactDeep || c.deep})`,
        contactText: '#ffffff',
        contactRing: c.accent,
        footerBg: c.deep,
        footerName: c.accent,
        footerText: 'rgba(255,255,255,0.95)',
        footerDivider: 'rgba(255,255,255,0.18)',
    };
}

// Light-background variant: off-white/cream poster with a soft tinted gradient,
// dark ink text, and the theme's accent used for headings, borders, pills and
// the (still colourful) contact CTA. `c.accentDeep` reads on the light bg.
function makeLightTheme(id, name, c) {
    const bg = c.bg || '#ffffff';
    const bgDeep = c.bgDeep || bg;
    const ink = c.ink || '#1c2430';
    const accent = c.accent;
    const accentDeep = c.accentDeep || accent;
    return {
        id,
        name,
        layout: c.layout || 'classic',
        orientation: c.orientation || 'portrait',
        light: true,
        posterBg: `linear-gradient(180deg, ${bg} 0%, ${bgDeep} 100%)`,
        frame: c.frame || accent,
        title: accentDeep,
        subtitle: 'rgba(30,41,59,0.72)',
        logoBg: '#ffffff',
        logoBorder: accent,
        logoText: accentDeep,
        badgeBg: c.badge || '#C0392B',
        badgeText: '#ffffff',
        badgeRing: accent,
        pillBg: '#ffffff',
        pillLabel: ink,
        pillValueBg: accent,
        pillValueText: '#ffffff',
        boxBg: c.panel || '#ffffff',
        boxBorder: c.boxBorder || accent,
        boxLabel: accentDeep,
        boxValue: ink,
        vacancyBg: c.vacancyPanel || '#ffffff',
        vacancyBorder: accent,
        vacancyNum: c.number || accentDeep,
        promo1: ink,
        promo2: accentDeep,
        contactBg: `linear-gradient(180deg, ${c.contactMid || accent}, ${c.contactDeep || accentDeep})`,
        contactText: '#ffffff',
        contactRing: accent,
        footerBg: c.footerBg || bgDeep,
        footerName: accentDeep,
        footerText: ink,
        footerDivider: 'rgba(0,0,0,0.10)',
    };
}

// A theme for one of the DESIGNS (designs/*): its own layout, drawn in `palette`. `posterBg` is only the placeholder the picker shows
// before a sample notice is available. `group` decides the heading it sits under in the picker (THEME_GROUPS).
function makeDesignTheme(id, name, design, palette, orientation = 'portrait') {
    return { id, name, design, orientation, group: orientation === 'landscape' ? 'wide' : 'designs', light: !!palette.light, palette, posterBg: palette.preview || palette.paper || palette.bg };
}

// What the picker groups the themes under, in this order.
export const THEME_GROUPS = ['designs', 'wide', 'classic'];

// ── The designs: each a different layout (portrait first, then the wide A4 ones) with at most two colourways ──
const DESIGN_THEMES = [
    makeDesignTheme('official-green', 'সরকারি নোটিশ', 'official', {
        light: true, paper: '#fbf7e9', paperDeep: '#f2ead0', ink: '#0f3d2e', accent: '#b8962e', ribbon: '#0f5a3f', ribbonDark: '#0a3a29', text: '#1b2b23', danger: '#a4262c',
    }),
    makeDesignTheme('official-maroon', 'সরকারি মেরুন', 'official', {
        light: true, paper: '#fbf5ec', paperDeep: '#f1e4d2', ink: '#5c1424', accent: '#b8862e', ribbon: '#7d1d36', ribbonDark: '#52101f', text: '#2c1a1e', danger: '#0f5a3f',
    }),
    makeDesignTheme('burst-yellow', 'বোল্ড হলুদ', 'burst', {
        light: true, paper: '#ffffff', hero: '#ffc400', heroDeep: '#f5a000', heroText: '#111827', ray: 'rgba(255,255,255,0.38)', accent: '#ffc400', ink: '#111827', muted: '#4b5563',
        number: '#d62828', cta: '#111827', ctaText: '#ffffff', footer: '#111827', footerText: '#ffffff', footerName: '#ffc400', preview: '#ffc400',
    }),
    makeDesignTheme('burst-red', 'বোল্ড লাল', 'burst', {
        light: true, paper: '#fff7f5', hero: '#d62839', heroDeep: '#9d0f20', heroText: '#ffffff', ray: 'rgba(255,255,255,0.16)', accent: '#ffd166', ink: '#1f1a1a', muted: '#6b5b5b',
        number: '#d62839', cta: '#d62839', ctaText: '#ffffff', footer: '#2b0a10', footerText: '#ffffff', footerName: '#ffd166', preview: '#d62839',
    }),
    makeDesignTheme('sidebar-teal', 'সাইডবার টিল', 'sidebar', {
        light: true, band: '#0f766e', bandDeep: '#0b4f4a', bandText: '#ffffff', accent: '#fbbf24', bg: '#f7fbfa', ink: '#0f2a2a', muted: '#51706e', line: '#d5e6e3', cta: '#0f766e', ctaText: '#ffffff', preview: '#0f766e',
    }),
    makeDesignTheme('sidebar-navy', 'সাইডবার নেভি', 'sidebar', {
        light: true, band: '#1e3a8a', bandDeep: '#142a63', bandText: '#ffffff', accent: '#fb923c', bg: '#f8faff', ink: '#0f1a3a', muted: '#5b6b8f', line: '#dbe3f5', cta: '#1e3a8a', ctaText: '#ffffff', preview: '#1e3a8a',
    }),
    makeDesignTheme('calendar-mint', 'ক্যালেন্ডার মিন্ট', 'calendar', {
        light: true, bg: '#f0fdf8', header: '#10b981', headerDeep: '#047857', cal1: '#0891b2', cal2: '#f97316', vacancyA: '#0d9488', vacancyB: '#047857', c1: '#6366f1', c2: '#ec4899',
        ink: '#0f2a24', muted: '#5b7a72', cta: '#f97316', ctaDeep: '#ea580c', preview: '#10b981',
    }),
    makeDesignTheme('calendar-peach', 'ক্যালেন্ডার পিচ', 'calendar', {
        light: true, bg: '#fff6ee', header: '#f97316', headerDeep: '#c2410c', cal1: '#0ea5e9', cal2: '#e11d48', vacancyA: '#ea580c', vacancyB: '#be123c', c1: '#14b8a6', c2: '#8b5cf6',
        ink: '#3a1d0a', muted: '#8c6a54', cta: '#0f766e', ctaDeep: '#115e59', preview: '#f97316',
    }),
    makeDesignTheme('neon-violet', 'নিয়ন ভায়োলেট', 'neon', {
        base: '#070b1f', glowA: '#7c3aed', glowB: '#06b6d4', glowC: '#ec4899', accent: '#22d3ee', ctaA: '#06b6d4', ctaB: '#7c3aed', preview: '#0b1030',
    }),
    makeDesignTheme('neon-sunset', 'নিয়ন সানসেট', 'neon', {
        base: '#140a1c', glowA: '#f43f5e', glowB: '#f59e0b', glowC: '#8b5cf6', accent: '#fbbf24', ctaA: '#f43f5e', ctaB: '#f59e0b', preview: '#1d0f26',
    }),
    makeDesignTheme('timeline-blue', 'টাইমলাইন', 'timeline', {
        light: true, bg: '#f5f8fd', top: '#2563eb', topDeep: '#1e3a8a', ink: '#0f1d3a', muted: '#5b6b8f', line: '#3b82f6', hot: '#e11d48', nodes: ['#2563eb', '#0891b2', '#7c3aed', '#d97706'], preview: '#2563eb',
    }),
    makeDesignTheme('timeline-green', 'টাইমলাইন গ্রিন', 'timeline', {
        light: true, bg: '#f4faf6', top: '#059669', topDeep: '#065f46', ink: '#0b2a1f', muted: '#587a6a', line: '#10b981', hot: '#dc2626', nodes: ['#059669', '#0891b2', '#7c3aed', '#d97706'], preview: '#059669',
    }),
    makeDesignTheme('magazine-red', 'ম্যাগাজিন', 'magazine', {
        light: true, paper: '#fbfaf6', ink: '#14110f', muted: '#6b645c', accent: '#c0392b', accentLight: '#f3b5ad', rule: '#14110f',
    }),
    makeDesignTheme('magazine-navy', 'ম্যাগাজিন নেভি', 'magazine', {
        light: true, paper: '#f7f5ef', ink: '#0f1e3d', muted: '#5c6682', accent: '#b8862e', accentLight: '#e8cf94', rule: '#0f1e3d',
    }),
    // ── Landscape (wide A4) ──
    makeDesignTheme('lsplit-teal', 'স্প্লিট', 'l-split', {
        light: true, hero: '#0f766e', heroDeep: '#0b4f4a', accent: '#fbbf24', bg: '#f4f9f8', ink: '#0f2a2a', muted: '#51706e', line: '#d5e6e3', cta: '#0f766e', footer: '#0f2a2a', footerText: '#ffffff', footerName: '#fbbf24', preview: '#0f766e',
    }, 'landscape'),
    makeDesignTheme('lbillboard-violet', 'বিলবোর্ড', 'l-billboard', {
        bg1: '#0a0a1f', bg2: '#1b1040', glowA: '#7c3aed', glowB: '#ec4899', accent: '#fbbf24', accent2: '#f97316', ink: '#1a1204', preview: '#140c33',
    }, 'landscape'),
    makeDesignTheme('lbillboard-ocean', 'বিলবোর্ড ওশান', 'l-billboard', {
        bg1: '#06121f', bg2: '#0b2a44', glowA: '#0ea5e9', glowB: '#14b8a6', accent: '#5eead4', accent2: '#38bdf8', ink: '#04202a', preview: '#0a2238',
    }, 'landscape'),
    makeDesignTheme('lcards-sky', 'কার্ড সারি', 'l-cards', {
        light: true, bg: '#f1f6fd', hero: '#2563eb', heroDeep: '#1e3a8a', ink: '#0f1d3a', muted: '#5f6f91', hot: '#e11d48', cta: '#2563eb', ctaDeep: '#1e3a8a', cards: ['#2563eb', '#10b981', '#8b5cf6', '#f59e0b'], preview: '#2563eb',
    }, 'landscape'),
    makeDesignTheme('lofficial-green', 'সরকারি ওয়াইড', 'l-official', {
        light: true, paper: '#fbf7e9', paperDeep: '#f2ead0', ink: '#0f3d2e', accent: '#b8962e', ribbon: '#0f5a3f', ribbonDark: '#0a3a29', text: '#1b2b23', danger: '#a4262c',
    }, 'landscape'),
];

// ── The original layout (PosterBase): only the ones that are genuinely different are kept — one per structure (classic / featured / banner),
// the black-and-white one for printing, and the wide one. The many recolours of the same layouts were removed. ──
const withGroup = (theme, group) => ({ ...theme, group });
const CLASSIC_THEMES = [
    withGroup(makeTheme('green-gold', 'সবুজ ও সোনালি', { layout: 'classic', deep: '#0b3324', mid: '#146b4a', accent: '#fcd34d', number: '#b8341f', contactMid: '#0e9f6e', contactDeep: '#065f46' }), 'classic'),
    withGroup(makeTheme('urgent-red', 'জরুরি লাল', { layout: 'featured', deep: '#5a1414', mid: '#a52020', accent: '#fcd34d', number: '#7a1010', badge: '#0b3324', contactMid: '#c0392b', contactDeep: '#7a1717' }), 'classic'),
    withGroup(makeTheme('royal-blue', 'রয়েল ব্লু', { layout: 'banner', deep: '#0b2447', mid: '#1a4d99', accent: '#ffd24d', number: '#c0392b', contactMid: '#1e63c4', contactDeep: '#123a7a' }), 'classic'),
    // Pure black & white (white background, monochrome — no colour anywhere): the one that prints cheaply.
    withGroup(makeTheme('mono-bw', 'কালো ও সাদা', {
        light: true, layout: 'classic', bg: '#ffffff', bgDeep: '#f4f4f4', ink: '#000000',
        accent: '#000000', accentDeep: '#000000', frame: '#000000', badge: '#111111',
        number: '#000000', boxBorder: '#000000', contactMid: '#1f1f1f', contactDeep: '#000000',
    }), 'classic'),
    // The original wide layout.
    withGroup(makeTheme('landscape-navy', 'ক্লাসিক ওয়াইড', {
        orientation: 'landscape', deep: '#0b2447', mid: '#123a7a', accent: '#ffd24d', number: '#c0392b',
        contactMid: '#1e63c4', contactDeep: '#123a7a',
    }), 'wide'),
];

// Picker order: the new designs, then the wide ones (new first, the original wide layout last), then the original portrait layouts.
export const TEMPLATES = [
    ...DESIGN_THEMES,
    ...CLASSIC_THEMES.filter((t) => t.group === 'wide'),
    ...CLASSIC_THEMES.filter((t) => t.group === 'classic'),
];

export const TEMPLATE_MAP = TEMPLATES.reduce((m, t) => {
    m[t.id] = t;
    return m;
}, {});

// Pinned by id (not by position in the list): the order of the picker can change without changing what everyone gets by default.
export const DEFAULT_TEMPLATE = 'green-gold';

export function getTheme(id) {
    return TEMPLATE_MAP[id] || TEMPLATE_MAP[DEFAULT_TEMPLATE];
}
